import type { RefObject } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { required } from "toucan";
import type { JumpMark } from "../model.ts";
import { JumpStrip } from "./JumpStrip.tsx";

/** The pane the grid scrolls in. */
function paneOf(grid: HTMLElement): HTMLElement {
  return required(grid.closest("main"), "main");
}

/** The grid's headings, which the strip's marks follow one for one. */
function headingsIn(grid: HTMLElement): HTMLElement[] {
  return [...grid.querySelectorAll<HTMLElement>(".heading")];
}

/**
 * Where the pane is scrolled to for a jump to `heading`: a heading's height
 * above it, so the heading has room over it at the top of the pane.
 */
function landing(heading: HTMLElement): number {
  return heading.offsetTop - heading.offsetHeight;
}

/**
 * Give the grid just the room under it that lets its pane scroll `top` to the
 * top of the pane, and no further, which a heading near the foot can't reach
 * otherwise. `given` is the room it has already.
 */
function makeRoom(grid: HTMLElement, top: number, given: number): number {
  const pane = paneOf(grid);
  const reach = pane.scrollHeight - pane.clientHeight - given;
  const room = Math.max(0, top - reach);
  grid.style.setProperty("--jump-room", `${String(room)}px`);
  return room;
}

/**
 * The jump strip wired to the grid it jumps through: it scrolls the grid's
 * pane to a heading and marks the heading scrolled to. It holds that mark
 * itself, so a drag down the strip redraws the strip and not the grid.
 */
export function HeadingJump({
  grid,
  marks,
  sized,
  label,
  class: className,
}: {
  grid: RefObject<HTMLElement | null>;
  marks: JumpMark[];
  sized: boolean;
  label: string;
  class: string;
}) {
  const [current, setCurrent] = useState(0);
  const room = useRef(0);

  // The grid outlives the strip, as when the order changes to one without it.
  useEffect(() => {
    const at = required(grid.current, "grid");
    return () => {
      at.style.removeProperty("--jump-room");
    };
  }, [grid]);

  // The last heading scrolled up to the top of the pane.
  useEffect(() => {
    // Held, not read from the ref, since the pane can scroll as the page
    // leaves, after the ref lets go and before this listener comes off.
    const at = required(grid.current, "grid");
    const view = paneOf(at);
    const place = () => {
      setCurrent(
        Math.max(
          0,
          headingsIn(at).findLastIndex(
            (heading) => landing(heading) <= view.scrollTop,
          ),
        ),
      );
    };
    place();
    view.addEventListener("scroll", place, { passive: true });
    return () => {
      view.removeEventListener("scroll", place);
    };
  }, [grid, marks]);

  return (
    <JumpStrip
      class={className}
      marks={marks}
      current={current}
      sized={sized}
      label={label}
      onJump={(index) => {
        const at = required(grid.current, "grid");
        // The first heading's jump goes back to the top, toolbar and all.
        const top =
          index === 0 ? 0 : landing(required(headingsIn(at)[index], "heading"));
        room.current = makeRoom(at, top, room.current);
        paneOf(at).scrollTop = top;
        setCurrent(index);
      }}
    />
  );
}
